import { Component, Inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ClaimDocumentService } from 'src/app/core/services/claim_document.service';
import { KycService } from 'src/app/core/services/kyc.service';
import { PersonalDocumentService } from 'src/app/core/services/personal_document.service';
import { UserService } from 'src/app/core/services/user.service';
import { ClaimsService } from 'src/app/core/services/claims.service';
import { BaseKycProviderComponent } from '../base-kyc-provider.component';
import { DocumentTypeEnum } from '../document-kyc-upload-modal.component';
import { KycDataService } from 'src/app/modules/client/pages/guide/services/kyc-data.service';
import { ActionPollingService } from 'src/app/modules/client/pages/guide/services/action-polling.service';
import { KycFileValidationService } from '../services/kyc-file-validation.service';
import { KycMessageService } from '../services/kyc-message.service';
import { TranslateService } from '@ngx-translate/core';
import {
  KycPreReviewModalComponent,
  KycPreReviewData,
  KycPreReviewIdentity,
  KycPreReviewResult,
  KycPreReviewTolerance,
} from '../../kyc-pre-review-modal/kyc-pre-review-modal.component';
import { normalizeCpf, normalizeDate, DocSignature } from 'src/lib/brdocs';
import { SerproCertProvider } from 'src/app/scan/scanner';
import { EventService } from 'src/app/core/services/event.service';
import { DeviceCapabilityService } from 'src/app/core/services/device-capability.service';
import { EventTypes } from 'src/app/core/models/events.model';
import { CameraStartInfo } from 'src/app/docpic/camera-stream.service';
import {
  SOMOS_OCR_PRE_REVIEW_TOLERANCE_DEFAULTS,
  SOMOS_OCR_SCORE_WEIGHTS_DEFAULTS,
} from './somos-ocr-precheck.config';
import {
  scorePreCheck,
  isValidCpf,
  PreCheckScoreResult,
  SomosOcrScoreWeights,
} from './somos-precheck-score';
import { ConfirmModalComponent } from '../../confirm-modal/confirm-modal.component';
import {
  KycCpfAckModalComponent,
  KycCpfAckData,
  KycCpfAckResult,
} from '../../kyc-cpf-ack-modal/kyc-cpf-ack-modal.component';
import { docFamily, DOC_META } from 'src/app/full-capture/doc-meta';
import { isPlausibleLegalName } from 'src/app/core/utils/legal-name.util';
import type { DocumentCheckFn, DocumentCheckInput, DocumentCheckOutcome } from 'src/app/full-capture/full-capture';
import { readIsDocumentClientConfig, toJpegBase64, withTimeout } from './is-document-check';

/**
 * Somos KYC v1 provider component.
 *
 * The document type-selection + upload screens are replaced by the **brdocs
 * guided capture** (`<brdocs-capture-flow>` — camera + upload, classification,
 * QR/MRZ extraction). When the guided flow emits its compiled result, this runs
 * the **pre-review** (step 1): compare the extracted data against the user's
 * stored PII and ask them to confirm it is their data and adopt it. Only after
 * confirmation does it map the capture onto the normal send path (upload the
 * image(s) + submit the kyc-check) using the base PROCESSING/SUCCESS/ERROR UI.
 */
@Component({
  selector: 'app-somos-kyc-v1-provider',
  templateUrl: '../base-kyc-provider.component.html',
  styleUrls: ['../base-kyc-provider.component.scss'],
})
export class SomosKycV1ProviderComponent extends BaseKycProviderComponent {
  override options = [
    { value: DocumentTypeEnum.ID_CARD, label: 'ID Card' },
    { value: DocumentTypeEnum.DRIVERS_LICENSE, label: 'Drivers License' },
  ];

  /** #7 In-flight guard: true from the moment a capture completion starts being handled until it
   * finishes, so a double-tap / re-entrant `(completed)` can't create duplicate uploads + checks. */
  private submitting = false;

  /** CPK-5103: until when new images are blocked (ISO, from /kyc_is_document), for the notice. */
  private isDocumentRetryAfter: string | null = null;

  /** #3 Telemetry: set true when the OCR pre-check could NOT run (transport failure / backend
   * fail-open) — distinct from a clean but empty read. Drives the degraded-mode confirmation. */
  private ocrDegraded = false;

  /**
   * Diagnostics snapshot of the current capture (document family/type, capture method, QR/OCR, and —
   * for CNH — the signature + SERPRO cert-source result). Computed once when the capture completes and
   * merged into every decision-outcome analytics event so a single event carries the full context for
   * troubleshooting a field failure. No PII (see {@link captureDiagnostics}).
   */
  private lastCaptureDiag: Record<string, unknown> | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: any,
    dialog: MatDialog,
    pds: PersonalDocumentService,
    cds: ClaimDocumentService,
    ks: KycService,
    us: UserService,
    dialogRef: MatDialogRef<SomosKycV1ProviderComponent>,
    kycService: KycDataService,
    validationService: KycFileValidationService,
    messageService: KycMessageService,
    translate: TranslateService,
    claimsService: ClaimsService,
    actionPollingService: ActionPollingService,
    router: Router,
    activatedRoute: ActivatedRoute,
    private events: EventService,
    private snackBar: MatSnackBar,
    private deviceCapability: DeviceCapabilityService
  ) {
    const serviceData = kycService.getData();
    if (serviceData != null) {
      data = serviceData;
    }

    super(
      data,
      dialog,
      pds,
      cds,
      ks,
      us,
      dialogRef,
      kycService,
      validationService,
      messageService,
      translate,
      claimsService,
      actionPollingService,
      router,
      activatedRoute
    );
    if (!data.provider) data.provider = 'somoskyc_v1';

    // Replace the TYPE-DOC + UPLOAD screens with the brdocs guided capture.
    this.useBrdocsCapture = true;

    // Link any "heavy on-device processing skipped" telemetry to this claim, so a
    // weak-device skip is attributable in the events (see DeviceCapabilityService).
    this.deviceCapability.setKycContext({ claimId: data.claim_id ?? null });
  }

  /**
   * Default (ms) for the guided-capture manual-fallback delay when the action carries no
   * `somosKyc.documentScanTimeSeconds` override. Mirrors the capture component's own
   * MANUAL_FALLBACK_MS default (7s) so the behaviour is identical whether the value is resolved
   * here or falls back inside `<brdocs-capture>`.
   */
  private static readonly DEFAULT_MANUAL_FALLBACK_MS = 7000;

  override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    // Resolve the per-action "Tirar foto" delay up-front (before the camera starts) so the manual
    // fallback timing already matches config when the guided flow mounts. `<brdocs-capture>` also
    // re-arms via ngOnChanges if this async read lands just after the camera loop started.
    // The is_document check (CPK-5103) is resolved alongside; the user is still on the welcome
    // screen when it lands, well before the first capture.
    const [fallbackMs, documentCheck] = await Promise.all([
      this.resolveDocumentScanTimeMs(),
      this.resolveDocumentCheck(),
    ]);
    this.manualFallbackMs = fallbackMs;
    this.documentCheck = documentCheck;
  }

  /**
   * CPK-5103: the is_document check for this action, or null when it is off. Reads
   * `cp_actions.config.isDocumentCheck` (same blob the server reads — see is-document-check.ts
   * for the client knobs and kyc-is-document/config.ts for the rest). Any lookup failure leaves
   * the check off, so the capture behaves exactly as before.
   */
  private async resolveDocumentCheck(): Promise<DocumentCheckFn | null> {
    try {
      const actionId = this.data.action_id;
      if (!actionId) return null;
      const { data } = await this.ks.supabase
        .from('cp_actions')
        .select('config')
        .eq('id', actionId)
        .single();
      const cfg = readIsDocumentClientConfig((data?.config as any)?.isDocumentCheck);
      if (!cfg.enabled) return null;
      return (input) => this.checkIsDocument(input, actionId, cfg.clientTimeoutMs, cfg.maxImageEdge);
    } catch (err) {
      console.warn('[SOMOS KYC V1] Failed to read isDocumentCheck from action config; check off:', err);
      return null;
    }
  }

  /**
   * CPK-5103: one capture through /kyc_is_document. Fail-open: a slow, failed or malformed answer
   * lets the image through — the check must never be why a real client can't finish KYC. Every
   * answer lands in the attempt trace (no PII: side, method, outcome, timings).
   */
  private async checkIsDocument(
    input: DocumentCheckInput,
    actionId: string,
    clientTimeoutMs: number,
    maxImageEdge: number,
  ): Promise<DocumentCheckOutcome> {
    const started = Date.now();
    const where = { side: input.side, method: input.method, source: input.source };
    try {
      const image = await toJpegBase64(input.image, maxImageEdge);
      const res = await withTimeout(
        this.ks.checkIsDocument({
          image,
          side: input.side,
          method: input.method,
          source: input.source,
          action_id: actionId,
          ...(this.data.claim_id ? { claim_id: this.data.claim_id } : {}),
        }),
        clientTimeoutMs,
      );
      // The server tells "this refusal started a block" (limit_blocked) apart from "a block is
      // running" (blocked); the capture flow reacts the same way to both.
      const known: Record<string, DocumentCheckOutcome> = {
        document: 'document',
        not_document: 'not_document',
        limit_restart: 'limit_restart',
        limit_proceed: 'limit_proceed',
        limit_blocked: 'blocked',
        blocked: 'blocked',
      };
      const outcome: DocumentCheckOutcome = (res && known[res.outcome]) || 'document';
      if (outcome === 'blocked') this.isDocumentRetryAfter = res?.retry_after ?? null;
      this.trace.log('classify', outcome === 'document' ? 'info' : 'warn', `is_document → ${res?.outcome ?? outcome}`, {
        ...where,
        outcome: res?.outcome ?? outcome,
        checkId: res?.check_id ?? null,
        failOpen: !!res?.fail_open,
        skipped: res?.skipped ?? null,
        consecutive: res?.consecutive_not_document ?? null,
        maxConsecutive: res?.max_consecutive ?? null,
        inWindow: res?.not_document_in_window ?? null,
        maxPerWindow: res?.max_per_window ?? null,
        retryAfter: res?.retry_after ?? null,
        ms: Date.now() - started,
      });
      return outcome;
    } catch (err) {
      this.trace.log('classify', 'warn', 'is_document check failed — letting the image through (fail-open)', {
        ...where,
        error: String(err),
        ms: Date.now() - started,
      });
      return 'document';
    }
  }

  /**
   * CPK-5103: the guided capture already went back to its welcome screen — after too many
   * "not a document" in a row, or because the client is blocked for sending too many overall.
   * Tell them why (and, when blocked, until when) before they start over. No KYC attempt is
   * used and nothing reaches Veriff or the review queue; the block itself is enforced by the
   * server, so a reload doesn't lift it.
   */
  override onBrdocsAction(e: { action: string; step?: string; method?: string; detail?: Record<string, unknown> }): void {
    super.onBrdocsAction(e);
    if (e.action !== 'not-document-limit' && e.action !== 'not-document-blocked') return;
    const blocked = e.action === 'not-document-blocked';
    const until = blocked ? this.formatRetryAfter(this.isDocumentRetryAfter) : null;
    this.dialog.open(ConfirmModalComponent, {
      data: blocked
        ? {
            titleIcon: 'block',
            title: 'KycNotDocumentBlockedTitle',
            // Already interpolated: the modal pipes it through `translate`, which returns a
            // non-key string unchanged.
            message: until
              ? this.translate.instant('KycNotDocumentBlockedMessage', { until })
              : 'KycNotDocumentBlockedMessageNoTime',
            confirmText: 'KycNotDocumentBlockedConfirm',
            hideCancelBtn: true,
            disableClose: true,
          }
        : {
            titleIcon: 'restart_alt',
            title: 'KycNotDocumentLimitTitle',
            message: 'KycNotDocumentLimitMessage',
            confirmText: 'KycNotDocumentLimitConfirm',
            hideCancelBtn: true,
            disableClose: true,
          },
      width: '90vw',
      maxWidth: '480px',
      maxHeight: '90vh',
      disableClose: true,
    });
  }

  /** "20/10, 14:35" in the user's language, or null when the server sent no usable time. */
  private formatRetryAfter(iso: string | null): string | null {
    const t = iso ? Date.parse(iso) : NaN;
    if (!Number.isFinite(t)) return null;
    const locale = this.translate.currentLang === 'en' ? 'en-GB' : 'pt-BR';
    return new Date(t).toLocaleString(locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  /**
   * Resolve the guided-capture manual-fallback delay (ms) for this action. Reads
   * `cp_actions.config.somosKyc.documentScanTimeSeconds` — an integer in SECONDS, in the same
   * `cp_actions.config` blob the OCR pre-check overrides live in — and converts it to ms. Falls
   * back to {@link DEFAULT_MANUAL_FALLBACK_MS} when there is no action, no override, an invalid
   * value, or the lookup fails.
   */
  private async resolveDocumentScanTimeMs(): Promise<number> {
    const fallback = SomosKycV1ProviderComponent.DEFAULT_MANUAL_FALLBACK_MS;
    try {
      if (!this.data.action_id) return fallback;
      const { data } = await this.ks.supabase
        .from('cp_actions')
        .select('config')
        .eq('id', this.data.action_id)
        .single();
      const seconds = ((data?.config as any)?.somosKyc ?? {}).documentScanTimeSeconds;
      if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0) {
        return Math.round(seconds * 1000);
      }
      return fallback;
    } catch (err) {
      console.warn('[SOMOS KYC V1] Failed to read documentScanTimeSeconds from action config; using default:', err);
      return fallback;
    }
  }

  /**
   * The brdocs guided flow finished. Run the pre-review (compare extracted vs
   * stored PII; confirm/adopt, explain-and-proceed, or restart) and, when the
   * user proceeds, hand off to the base upload + submit path.
   */
  /** Emit KYC_CAMERA_STREAM_STARTED once per flow with the device's camera details. */
  override onBrdocsCameraStarted(info: CameraStartInfo): void {
    this.events
      .sendEventWithCaseId(
        EventTypes.KYC_CAMERA_STREAM_STARTED,
        this.data.claim_id,
        info as unknown as Record<string, any>,
      )
      .catch(() => {});
  }

  override async onBrdocsCompleted(result: any): Promise<void> {
    // #7 Double-submit / re-entrancy guard: a second `(completed)` (double-tap, host re-emit) while
    // the first is still being handled would create a duplicate cp_kyc_check + duplicate uploads.
    if (this.submitting) {
      this.trace.log('capture', 'warn', 'Ignoring re-entrant capture completion (submit already in flight)', null);
      return;
    }
    this.submitting = true;
    try {
      // #9 / #16 Surface non-blocking capture warnings (QR unreadable → OCR-only; invalid QR
      // signature) before anything else, so the user is told the read may be less trustworthy.
      this.surfaceCaptureWarnings(result);

      // Comprehensive capture diagnostics — one trace line + a snapshot carried into every decision
      // event so an agent can see, in one place, WHY a field failed (document/method/QR/OCR/signature/
      // SERPRO cert source). No PII (document type/name, counts, opaque cert ids, statuses only).
      this.lastCaptureDiag = this.captureDiagnostics(result);
      this.trace.log(
        'qr',
        this.captureDiagLevel(result),
        'Capture diagnostics (document · method · QR/OCR · signature · cert source)',
        this.lastCaptureDiag,
      );

      const isQr = !!result?.fromQrCode;

      // v2 unified stepper: switch to the PROCESSING step list the moment the capture completes,
      // so the pre-check upload / OCR read / confirmation modals all run over visible steps
      // (the modals open on top of it) instead of behind the capture screen.
      this.somosStepperStart();

      // QR path: identity comes from the decoded QR/MRZ — existing behaviour, unchanged.
      // Pre-review (strict, no tolerance) then upload + submit via the base path.
      if (isQr) {
        // The read happened during capture (QR decode) — the step completes instantly.
        this.somosStepAdvance('READING', 'CONFIRMING');
        const extracted = this.identityFromResult(result);
        // #2 CPF policy is per document family. CNH/CIN QR docs MUST carry a valid CPF (hard reject +
        // retake). For ack-if-missing families (RG/pescador/certidão/outro) a missing/invalid CPF opens
        // the acknowledgment modal instead of dead-ending; only proceed when the user attests.
        if (!isValidCpf(extracted.cpfDigits)) {
          if (!(await this.handleMissingCpf(extracted, result))) return;
        }
        // Multi-version CNH QRs: reader-cnh decodes older/newer generations, but versions 1/3/4 are
        // implemented from decompilation with no reference sample. Accept their (valid-CPF) identity —
        // don't dead-end a legitimate holder — but flag the attempt for manual review. The flag rides
        // the capture telemetry (cnhVersionVerified in the diagnostics above) so an agent can confirm.
        if (result?.versionVerified === false) {
          this.trace.log(
            'precheck',
            'warn',
            'CNH QR version not confirmed against a reference sample — proceeding, flagged for manual review',
            { qrKind: result?.qrKind ?? null, cnhVersionVerified: false },
          );
        }
        await this.runPreReviewAndSubmit(result, extracted, undefined, false);
        return;
      }

      // No-QR path: there is no QR to trust. Upload the captured document to storage FIRST so the
      // standalone Textract lambda can OCR the stored doc, compare against the PII on file, then
      // submit reusing the already-uploaded ids (no second upload). Both run under READING.
      if (!(await this.prepareCapturedFiles(result))) {
        // #8 No usable image — restart capture instead of silently dead-ending.
        this.trace.log('capture', 'warn', 'Capture result had no usable image — restarting capture', null);
        this.toast('KycNoUsableImageRetry');
        this.restartCapture();
        return;
      }
      let uploaded: { frontId: string; backId?: string };
      try {
        uploaded = await this.uploadFilesWithRetry();
      } catch (err) {
        // Upload failed — fall back to the base flow so its retry/error UI handles it.
        // uploadAndProcess re-derives a coherent step list (read/confirm done, sending active —
        // the retried upload is part of "Sending your verification").
        console.warn('[SOMOS KYC V1] pre-check upload failed, using base flow:', err);
        await this.uploadAndProcess();
        return;
      }
      this.ocrDegraded = false;
      const extracted = await this.ocrIdentityFromFrontId(uploaded.frontId, uploaded.backId);
      this.somosStepAdvance('READING', 'CONFIRMING');

      // #3 Fail-open made visible: the pre-check could not run (transport failure / backend
      // fail-open). Keep failing open (Veriff/SERPRO stay authoritative) but confirm with the user
      // first, so a wrong-person/bad document doesn't sail past the client pre-check with no feedback.
      if (this.ocrDegraded) {
        const proceed = await this.confirmDegradedCheck();
        if (!proceed) {
          this.trace.log('precheck', 'warn', 'Degraded pre-check: user chose to retake the document', null);
          void this.logPreReviewOutcome('degraded_restart', extracted);
          this.restartCapture();
          return;
        }
        this.trace.log('precheck', 'warn', 'Degraded pre-check: user confirmed and proceeded (Veriff/SERPRO authoritative)', null);
        void this.logPreReviewOutcome('degraded_proceed', extracted);
        await this.runPreReviewAndSubmit(result, extracted, uploaded, true);
        return;
      }

      // #2 / #11 CPF policy is per document family. The OCR ran successfully, so for CNH/CIN a valid
      // CPF is mandatory (hard reject + retake). For ack-if-missing families (RG/pescador/certidão/
      // outro) a missing/invalid CPF opens the acknowledgment modal; proceed only when the user attests.
      if (!isValidCpf(extracted.cpfDigits)) {
        if (!(await this.handleMissingCpf(extracted, result))) return;
      }

      await this.runPreReviewAndSubmit(result, extracted, uploaded, true);
    } catch (e) {
      // #10 Surface unexpected failures via the base error state (with retry) instead of only
      // logging to the console and leaving the user stuck on the capture screen.
      console.error('[SOMOS KYC V1] Failed to handle brdocs result:', e);
      this.trace.log('capture', 'error', 'Unexpected error handling capture result', { error: String(e) });
      this.surfaceError();
    } finally {
      this.submitting = false;
    }
  }

  /**
   * #2 / #11 Hard reject: an accepted document MUST carry a valid, readable CPF. Explain to the user
   * why the document was rejected and restart capture, reusing the pre-review 'restart' wiring
   * ({@link restartCapture}). Distinguishes "no CPF at all" from "CPF present but unreadable/invalid".
   */
  private async rejectMissingCpf(extracted: KycPreReviewIdentity): Promise<void> {
    const hadDigits = !!(extracted.cpfDigits && extracted.cpfDigits.replace(/\D/g, ''));
    const messageKey = hadDigits ? 'KycCpfUnreadableRejected' : 'KycCpfRequiredRejected';
    const reason = hadDigits ? 'invalid_cpf' : 'no_cpf';
    this.trace.log('precheck', 'warn', 'Document REJECTED — no valid CPF (restarting capture)', {
      cpf: this.maskCpf(extracted.cpfDigits),
      reason,
    });
    void this.logPreReviewOutcome('reject', extracted, undefined, undefined, reason);
    this.somosStepActionNeeded('CONFIRMING', true);
    const ref = this.dialog.open(ConfirmModalComponent, {
      data: {
        titleIcon: 'error',
        title: 'KycCpfRequiredRejectedTitle',
        message: messageKey,
        confirmText: 'KycRetakeDocument',
        hideCancelBtn: true,
        disableClose: true,
      },
      width: '90vw',
      maxWidth: '480px',
      maxHeight: '90vh',
      disableClose: true,
    });
    await firstValueFrom(ref.afterClosed());
    this.restartCapture();
  }

  /**
   * #2 CPF policy per document family. CNH/CIN carry a signed QR that MUST encode a valid CPF, so a
   * missing/invalid CPF is a hard reject (`required`). RG / Carteira de Pescador / Certidão / Outro
   * (and any unknown family — the safe default) legitimately may not have a readable CPF, so they use
   * the acknowledgment path (`ackIfMissing`). Single source of truth: read the policy straight from
   * DOC_META so it can never drift from the document-family metadata; fall back to `ackIfMissing`
   * for unknown families / missing meta.
   */
  private cpfPolicyFor(family: string): 'required' | 'ackIfMissing' {
    return DOC_META[family]?.cpf ?? 'ackIfMissing';
  }

  /**
   * #2 Handle a missing/invalid CPF according to the document family's policy. Returns true when the
   * caller should PROCEED (submit), false when it was fully handled here (hard reject → retake, or the
   * user cancelled the acknowledgment → retake).
   *  - `required` (CNH/CIN): hard reject + retake (existing modal, not a silent loop).
   *  - `ackIfMissing` (RG/pescador/certidão/outro): open the acknowledgment modal. Both attestations →
   *    record the acknowledgment (analytics, fire-and-forget) and proceed. Cancel → retake.
   */
  private async handleMissingCpf(extracted: KycPreReviewIdentity, result: any): Promise<boolean> {
    const family = docFamily(String(result?.type ?? ''));
    if (this.cpfPolicyFor(family) === 'required') {
      await this.rejectMissingCpf(extracted);
      return false;
    }
    this.somosStepActionNeeded('CONFIRMING', true);
    const ref = this.dialog.open(KycCpfAckModalComponent, {
      data: { documentTitle: result?.documentName ?? undefined } as KycCpfAckData,
      width: '90vw',
      maxWidth: '480px',
      maxHeight: '90vh',
      disableClose: true,
    });
    const decision = (await firstValueFrom(ref.afterClosed())) as KycCpfAckResult | undefined;
    this.somosStepActionNeeded('CONFIRMING', false);
    if (decision?.acknowledged !== true) {
      this.trace.log('precheck', 'warn', 'CPF acknowledgment declined — restarting capture', { family });
      this.restartCapture();
      return false;
    }
    this.trace.log('precheck', 'warn', 'CPF unreadable — user acknowledged and proceeded', {
      family,
      cpfPolicy: this.cpfPolicyFor(family),
      cpf: this.maskCpf(extracted.cpfDigits),
    });
    void this.logCpfAcknowledged(family, extracted);
    return true;
  }

  /**
   * #2 Record the RG/no-CPF acknowledgment as an analytics event. Reuses the existing
   * KYC_PRECHECK_NOT_CONFIRMED event type with a distinct `outcome: 'rg_cpf_acknowledged'` so no new
   * EventType enum / DB migration is needed. Fire-and-forget — recording must never block submit.
   */
  private async logCpfAcknowledged(family: string, extracted: KycPreReviewIdentity): Promise<void> {
    const detail = {
      outcome: 'rg_cpf_acknowledged',
      family,
      cpfPolicy: this.cpfPolicyFor(family),
      provider: 'somoskyc_v1',
      extractedCpf: this.maskCpf(extracted.cpfDigits),
      attestedAt: new Date().toISOString(),
      // Full capture diagnostics (document · method · QR/OCR · signature · SERPRO cert source).
      capture: this.lastCaptureDiag,
    };
    console.warn('[SOMOS KYC V1] CPF acknowledgment:', detail);
    try {
      await this.events.sendEventWithCaseId(
        EventTypes.KYC_PRECHECK_NOT_CONFIRMED,
        this.data.claim_id,
        detail,
      );
    } catch (err) {
      console.warn('[SOMOS KYC V1] Failed to log CPF acknowledgment event (non-fatal):', err);
    }
  }

  /** Mask a CPF to its last two digits for analytics (never log the full number). */
  private maskCpf(digits: string | null): string | null {
    const d = (digits ?? '').replace(/\D/g, '');
    if (!d) return null;
    return `***.***.***-${d.slice(-2)}`;
  }

  /**
   * Mask a name for logging: never the full name — only the first token plus the total length (enough
   * to spot a wrong-person read or an empty read without leaking PII).
   */
  private maskName(name: string | null | undefined): string | null {
    const n = (name ?? '').trim();
    if (!n) return null;
    const first = n.split(/\s+/)[0];
    return `${first}… (${n.length} chars)`;
  }

  /**
   * Build the comprehensive, PII-safe capture diagnostics snapshot: document family/type + capture
   * method + QR/OCR provenance + (CNH only) the signature verification result and the SERPRO
   * cert-source diagnostic. The cert source is read from {@link SerproCertProvider.lastCertSourceDiag},
   * populated by the scanner during signature verification; it is only attached for CNH documents that
   * actually carried a signature, so a stale value can't bleed onto a no-QR / non-CNH capture.
   */
  private captureDiagnostics(result: any): Record<string, unknown> {
    const family = docFamily(String(result?.type ?? ''));
    const fields: Array<{ source?: string; label?: string; value?: string }> = Array.isArray(result?.fields)
      ? result.fields
      : [];
    const sig = result?.signature as DocSignature | null | undefined;
    // Only trust the (static) cert-source diagnostic for a CNH that carried a signature this attempt.
    const cert = family === 'cnh' && sig ? SerproCertProvider.lastCertSourceDiag : null;
    return {
      // #4 Document + capture context.
      documentType: result?.type ?? null,
      documentFamily: family,
      documentName: result?.documentName ?? null, // a doc-type name (e.g. "CNH-e"), not the holder
      recognized: result?.recognized ?? null,
      captureMethod: result?.method ?? null, // camera | upload
      fromQrCode: !!result?.fromQrCode,
      // #3 QR / OCR provenance.
      qrDecoded: !!result?.fromQrCode,
      // The decoded QR document kind ('cnh'/'cin'/'mrz', or 'text' when a generic/unrecognised QR
      // was claimed by the fallback parser — the tell for a junk front-side QR carrying no identity).
      qrKind: result?.qrKind ?? null,
      // For a CNH QR, reader-cnh's confidence in the QR version's byte layout: `false` means an
      // unverified generation (v1/v3/v4, decoded from decompilation with no reference sample) whose
      // fields are accepted but flagged for manual review. `null` when no versioned QR was read.
      cnhVersionVerified: result?.versionVerified ?? null,
      warning: result?.warning ?? null,
      fieldsFromQr: fields.filter((f) => f?.source === 'qr').length,
      fieldsFromOcr: fields.filter((f) => f?.source === 'ocr').length,
      // Raw decoded QR content — every QR-sourced field, verbatim (label + value), so an agent can see
      // exactly what the QR read (e.g. a URL from a physical CNH front vs. real CNH-e identity data).
      // NOTE: unmasked by design — for a genuine CNH-e/CIN this includes the holder's CPF/name/RG.
      qrRawFields: fields
        .filter((f) => f?.source === 'qr')
        .map((f) => ({ label: f.label ?? null, value: this.truncateForLog(f.value) })),
      // #5 CPF policy that will be applied for this family.
      cpfPolicy: this.cpfPolicyFor(family),
      // #2 Signature / verification.
      signatureStatus: sig?.status ?? null,
      signatureDetail: sig?.detail ?? null,
      verifierId: sig?.verifierId ?? null,
      candidateCertCount: sig?.candidateCertCount ?? null,
      triedCertIds: Array.isArray(sig?.triedCertIds) ? sig!.triedCertIds!.slice(0, 20) : null,
      matchedCertId: sig?.matchedCertId ?? null,
      // #1 SERPRO cert source — the key signal for why field verification can fail.
      certSource: cert?.source ?? null,
      certLiveError: cert?.liveError ?? null,
      certLiveCount: cert?.liveCount ?? null,
      certCacheCount: cert?.cacheCount ?? null,
      certBundleCount: cert?.bundleCount ?? null,
      certEcUsableCount: cert?.ecUsableCount ?? null,
    };
  }

  /**
   * Bound a raw QR value before it goes into an event/trace log. QR identity payloads are short, but a
   * garbled/unsupported binary QR decoded as text can be large — cap it so a single read can't bloat the
   * event. Returns non-strings untouched and flags truncation so the log stays honest.
   */
  private truncateForLog(value: unknown, max = 2000): unknown {
    if (typeof value !== 'string') return value ?? null;
    return value.length > max ? `${value.slice(0, max)}…[+${value.length - max} chars]` : value;
  }

  /** Trace level for the capture-diagnostics line: error on a bad signature, warn on a weak read. */
  private captureDiagLevel(result: any): 'info' | 'warn' | 'error' {
    const family = docFamily(String(result?.type ?? ''));
    const sig = result?.signature as DocSignature | null | undefined;
    const cert = family === 'cnh' && sig ? SerproCertProvider.lastCertSourceDiag : null;
    if (sig?.status === 'invalid') return 'error';
    if (cert && (cert.source === 'live-failed' || cert.source === 'empty')) return 'warn';
    if (result?.warning) return 'warn';
    return 'info';
  }

  /**
   * #3 Degraded-mode confirmation: the OCR pre-check couldn't run, so ask the user to confirm this is
   * really their document before we fall open to Veriff/SERPRO. Returns true to proceed.
   */
  private async confirmDegradedCheck(): Promise<boolean> {
    this.somosStepActionNeeded('CONFIRMING', true);
    const ref = this.dialog.open(ConfirmModalComponent, {
      data: {
        titleIcon: 'help',
        title: 'KycDegradedCheckTitle',
        message: 'KycDegradedCheckMessage',
        confirmText: 'KycDegradedCheckConfirm',
        cancelButton: 'KycRetakeDocument',
        disableClose: true,
      },
      width: '90vw',
      maxWidth: '480px',
      maxHeight: '90vh',
      disableClose: true,
    });
    const proceed = (await firstValueFrom(ref.afterClosed())) === true;
    this.somosStepActionNeeded('CONFIRMING', false);
    return proceed;
  }

  /**
   * #9 / #16 Surface non-blocking capture warnings as a toast: the QR could not be decoded (data is
   * OCR-only and may be wrong), and/or the document's QR signature failed validation. The pre-review
   * modal has no note slot, so a snackbar is used. Only a definitively `invalid` signature is flagged.
   */
  private surfaceCaptureWarnings(result: any): void {
    const notes: string[] = [];
    if (result?.warning) {
      this.trace.log('capture', 'warn', 'QR read warning surfaced to user (OCR-only data)', {
        warning: String(result.warning),
      });
      notes.push(this.translate.instant('KycQrCouldNotValidateWarning'));
    }
    if (result?.signature?.status === 'invalid') {
      this.trace.log('capture', 'warn', 'QR signature invalid — surfaced to user', {
        detail: result?.signature?.detail ?? null,
      });
      notes.push(this.translate.instant('KycQrSignatureInvalidWarning'));
    }
    if (notes.length) this.snackBar.open(notes.join(' '), undefined, { duration: 7000 });
  }

  /** Non-blocking toast helper (message via a translation key). */
  private toast(key: string): void {
    this.snackBar.open(this.translate.instant(key), undefined, { duration: 6000 });
  }

  /**
   * #10 Drive the base processing-error state (with a Try-again button) so an unexpected failure in
   * the capture handoff surfaces instead of hanging silently. The retry (`processError`) resets back
   * to the brdocs capture flow.
   */
  private surfaceError(): void {
    this.steps = 'PROCESSING';
    this.hasProcessingError = true;
    this.isProcessingIndeterminate = false;
    // Flag the step the failure happened on so the stepper shows where it broke.
    this.errorInStep = this.currentProcessingStep;
    this.updateProcessingStep(this.currentProcessingStep, false, true);
    this.currentError = this.messageService.getErrorDetails('UNKNOWN_ERROR');
  }

  /**
   * v2: the stepper replaces the capture screen the moment `(completed)` fires, which destroys the
   * `<brdocs-capture-flow>` (it renders only while `steps` is TYPE-DOC/UPLOAD) — so `recomecar()`
   * on the stale ViewChild ref would be a no-op. Every restart decision (pre-review "not my
   * document", CPF-ack declined, missing-CPF retake, degraded retake, unusable image) instead
   * resets `steps` to the capture screen so a fresh guided flow mounts from the beginning.
   */
  private restartCapture(): void {
    this.hasProcessingError = false;
    this.errorInStep = null;
    this.currentError = null;
    this.isProcessingIndeterminate = true;
    this.steps = 'TYPE-DOC';
  }

  /**
   * Shared pre-review + submit. When the document yields comparable identity data, open the
   * pre-review modal (confirm/adopt, explain-and-proceed, or restart). `preUploaded` is set on the
   * no-QR path (the document is already in storage) so the submit reuses those ids; `useTolerance`
   * applies the configurable rough-match tolerance (no-QR only) — the QR path stays strict.
   */
  private async runPreReviewAndSubmit(
    result: any,
    extracted: KycPreReviewIdentity,
    preUploaded: { frontId: string; backId?: string } | undefined,
    useTolerance: boolean,
  ): Promise<void> {
    if (extracted.fullName || extracted.cpf || extracted.dobIso) {
      const user = await this.loadUserIdentity();

      // The algorithm's reading of the document vs the data on file — the basis of every pre-review.
      // PII masked — first token + length for names, last-two-digits for CPF; DOB kept for mismatch diagnosis.
      this.trace.log('precheck', 'info', `Pre-review: document (${useTolerance ? 'OCR' : 'QR'}) vs on-file`, {
        documentName: this.maskName(extracted.fullName),
        documentCpf: this.maskCpf(extracted.cpfDigits),
        documentDob: extracted.dobIso,
        onFileName: this.maskName(user.fullName),
        onFileCpf: this.maskCpf(user.cpfDigits),
        onFileDob: user.dobIso,
      });

      // QR path only: some documents (e.g. the CIN) encode CPF + DOB but NOT the holder's name in
      // their QR code. When the QR carried no name, there is nothing about the name to confirm — so
      // if the CPF and DOB it did carry already match the user's stored PII, the pre-review modal
      // adds no value. Skip it and proceed straight to submission (nothing to adopt either).
      if (!useTolerance && this.qrMatchesWithoutName(extracted, user)) {
        this.trace.log('precheck', 'success', 'QR carried no name; CPF + DOB match on file — skipping confirmation', {
          documentCpf: this.maskCpf(extracted.cpfDigits),
          documentDob: extracted.dobIso,
        });
        void this.logPreReviewOutcome('auto_proceed', extracted, user);
        await this.submitFinal(result, preUploaded);
        return;
      }

      // No-QR path only: a weighted confidence score decides whether the document clearly belongs to
      // the user. A high score auto-proceeds (skip the modal); anything ambiguous/low opens it. The QR
      // path (useTolerance=false) keeps its original behaviour and always opens the modal.
      let score: PreCheckScoreResult | undefined;
      if (useTolerance) {
        const weights = await this.resolveOcrScoreWeights();
        score = scorePreCheck(extracted, user, weights);
        this.trace.log('precheck', 'info', `Match score: ${score.score} → ${score.decision}`, {
          score: score.score,
          decision: score.decision,
          breakdown: score.breakdown,
        });
        if (score.decision === 'proceed') {
          // Clearly their document — skip the pre-review and submit. A high score means the document
          // already matches their stored PII, so do NOT adopt/overwrite anything.
          // Fire-and-forget the analytics event: recording it must never block or delay the KYC
          // submission, even if /event_push is slow, errors, or the event type isn't seeded yet.
          this.trace.log('precheck', 'success', 'Auto-proceed: document clearly matches (no modal shown)', null);
          void this.logPreReviewOutcome('auto_proceed', extracted, user, score);
          await this.submitFinal(result, preUploaded);
          return;
        }
      }

      const tolerance = useTolerance ? await this.resolveOcrTolerance() : undefined;
      // Amber the "Confirming your details" step while the decision modal floats over the stepper.
      this.somosStepActionNeeded('CONFIRMING', true);
      const ref = this.dialog.open(KycPreReviewModalComponent, {
        data: {
          extracted,
          user,
          documentTitle: result?.documentName ?? 'document',
          ...(tolerance ? { tolerance } : {}),
          // "Amend my details" + fill-gaps confirm are the no-QR (OCR) behaviour only. On the QR path
          // (useTolerance=false) the modal stays exactly as the original (no amend, confirm adopts).
          allowAmend: useTolerance,
        } as KycPreReviewData,
        width: '90vw',
        maxWidth: '560px',
        maxHeight: '90vh',
        disableClose: true,
      });
      this.trace.log('precheck', 'info', 'Showing pre-review modal for user confirmation', {
        allowAmend: useTolerance,
      });
      const decision = (await firstValueFrom(ref.afterClosed())) as KycPreReviewResult | undefined;
      this.somosStepActionNeeded('CONFIRMING', false);
      if (!decision) {
        // Dismissed without a decision — the capture screen no longer exists behind the stepper,
        // so restart the guided flow rather than stranding the user on a paused step list.
        this.trace.log('precheck', 'warn', 'Pre-review modal dismissed — restarting capture', null);
        this.restartCapture();
        return;
      }

      // The pre-review analytics are fire-and-forget everywhere: a failed/slow/unseeded event must
      // never block the user from continuing (restart / proceed / amend / confirm).
      if (decision.action === 'restart') {
        this.trace.log('precheck', 'warn', 'User REJECTED the document — restarting capture', {
          note: decision.explanation ?? null,
        });
        void this.logPreReviewOutcome('restart', extracted, user, score, decision.explanation);
        this.restartCapture(); // back to the start of the guided flow
        return;
      }
      if (decision.action === 'proceed') {
        // Not their document, but proceeding with an explanation — do NOT adopt.
        this.trace.log('precheck', 'warn', 'User proceeded despite mismatch (with explanation)', {
          note: decision.explanation,
        });
        void this.logPreReviewOutcome('proceed', extracted, user, score, decision.explanation);
      }
      if (decision.action === 'reconcile') {
        // No-QR (OCR) path: the user stepped through each differing field and picked the correct
        // value (document / account / typed). OVERWRITE their PII with exactly those reconciled
        // values; fields that already matched are left untouched (null in `resolved`).
        this.trace.log('precheck', 'info', 'User edited the suggestion (reconciled fields → overwriting PII)', {
          // PII masked — enough to see WHICH fields changed without leaking the values.
          algoSuggested: { name: this.maskName(extracted.fullName), cpf: this.maskCpf(extracted.cpfDigits), dob: extracted.dobIso },
          userChose: {
            name: this.maskName(decision.resolved.fullName),
            cpf: this.maskCpf(decision.resolved.cpf ? decision.resolved.cpf.replace(/\D/g, '') : null),
            dob: decision.resolved.dobIso,
          },
        });
        void this.logPreReviewOutcome('reconcile', extracted, user, score);
        await this.adoptExtractedPii(decision.resolved, user, { overwrite: true });
      }
      if (decision.action === 'confirm') {
        // QR path only: the QR-decoded data is trusted, so confirm overwrites the user's PII to
        // match the document. (The no-QR path no longer emits 'confirm' — it uses 'reconcile'.)
        this.trace.log('precheck', 'success', 'User confirmed the document data (adopting it)', {
          name: this.maskName(extracted.fullName),
          cpf: this.maskCpf(extracted.cpfDigits),
          dob: extracted.dobIso,
        });
        await this.adoptExtractedPii(extracted, user, { overwrite: !useTolerance });
      }
    }

    await this.submitFinal(result, preUploaded);
  }

  /** Submit — reuse the already-uploaded docs on the no-QR path; upload now on the QR path. */
  private async submitFinal(
    result: any,
    preUploaded: { frontId: string; backId?: string } | undefined,
  ): Promise<void> {
    if (preUploaded) {
      await this.uploadAndProcess(preUploaded);
    } else {
      await this.submitCapturedDocument(result);
    }
  }

  /** Turn the captured image(s) into Files and run the base upload + submit (QR path). */
  private async submitCapturedDocument(result: any): Promise<void> {
    if (!(await this.prepareCapturedFiles(result))) return;
    await this.uploadAndProcess();
  }

  /**
   * Map the captured brdocs result onto the base upload state (document type, CNH format, front/back
   * Files + previews). Returns false when there is no usable front image.
   */
  private async prepareCapturedFiles(result: any): Promise<boolean> {
    const type = String(result?.type ?? '');
    this.documentType = type.includes('cnh')
      ? DocumentTypeEnum.DRIVERS_LICENSE
      : DocumentTypeEnum.ID_CARD;
    this.cnhType = result?.fromQrCode ? 'digital' : 'physical';
    // A PICTURE of a digital CNH (photographed screen/printout), accepted on its signed
    // QR data. Veriff cannot verify such an image and will reject it, so the provider
    // must postpone the check to manual review rather than ask the user to try again —
    // they have nothing better to send. See the capture flow's provenance routing.
    this.digitalPictureCapture = !!result?.digitalPicture;
    // Our own SERPRO-signature verdict on the decoded QR (reader-cnh). `verified` is
    // authoritative; `invalid` / `unverified` / `unsupported` mean we could NOT validate
    // the signature — not that the document is forged (a legacy RSA doc, or a retired /
    // not-yet-bundled SERPRO cert, still yields a genuine CNH-e). The lambda decides what
    // to do with that via the `document_signature` validation + its postpone token, so it
    // has to be told. Null when no QR was decoded (a plain photo of a physical document).
    this.documentSignatureStatus = result?.signature?.status ?? null;

    // Prefer the RAW source bytes (original upload / HEIC-converted / camera JPEG).
    // Fall back to the processed data URL only if no raw blob is present.
    this.frontImageFile = result?.frontRaw
      ? new File([result.frontRaw], result.frontRawName ?? 'kyc-front.jpg', { type: result.frontRaw.type || 'image/jpeg' })
      : result?.frontImage
        ? await this.dataUrlToFile(result.frontImage, 'kyc-front.jpg')
        : null;
    this.backImageFile = result?.backRaw
      ? new File([result.backRaw], result.backRawName ?? 'kyc-back.jpg', { type: result.backRaw.type || 'image/jpeg' })
      : result?.backImage
        ? await this.dataUrlToFile(result.backImage, 'kyc-back.jpg')
        : null;
    this.frontFileMetadata = result?.frontMeta ?? null;
    this.backFileMetadata = result?.backMeta ?? null;

    if (!this.frontImageFile) {
      console.warn('[SOMOS KYC V1] brdocs result had no image — staying on capture.');
      return false;
    }
    this.frontImage = result?.frontImage ?? '';
    this.backImage = result?.backImage ?? '';
    return true;
  }

  /** Map the brdocs compiled result's fields to the normalised identity. */
  private identityFromResult(result: any): KycPreReviewIdentity {
    let fields: Array<{ label: string; value: string; source?: 'qr' | 'ocr' }> = result?.fields ?? [];
    // CPK-4341: on the QR path, only the SIGNED QR is a trusted identity source. The capture
    // backfills fields the QR didn't carry with OCR reads (source: 'ocr') for display — but a
    // confirm here adopts the identity with overwrite, and the CIN QR carries no holder name, so
    // the OCR-garbled bilingual label line ("NOME / NAME" → "! Name S") became the client's legal
    // name in 20 production cases. Dropping OCR fields re-arms the designed no-name QR flow:
    // skip the modal when CPF + DOB match, or show it without a name row.
    if (result?.fromQrCode) {
      const dropped = fields.filter((f) => f.source === 'ocr').map((f) => f.label);
      if (dropped.length) {
        fields = fields.filter((f) => f.source !== 'ocr');
        this.trace.log('precheck', 'info', 'QR path: OCR-sourced field(s) ignored for identity (only the signed QR is trusted)', {
          dropped,
        });
      }
    }
    const find = (labels: string[]): string | null => {
      for (const f of fields) {
        const fl = this.fold(f.label);
        if (labels.some((l) => fl === this.fold(l) || fl.includes(this.fold(l))) && f.value) {
          return f.value.trim();
        }
      }
      return null;
    };
    const fullName = find(['nome completo', 'nome', 'name']);
    const cpfRaw = find(['cpf']);
    const dobRaw = find(['data de nascimento', 'nascimento', 'birth']);
    const cpf = cpfRaw ? normalizeCpf(cpfRaw) : null;
    return {
      fullName,
      cpf,
      cpfDigits: cpf ? cpf.replace(/\D/g, '') : null,
      dobIso: dobRaw ? normalizeDate(dobRaw) : null,
    };
  }

  /**
   * No-QR path: OCR the already-uploaded document side(s) (by their cp_personal_document ids) via the
   * standalone /somos_kyc_ocr_extract lambda and map the extracted CPF / DOB / full name onto the
   * pre-review identity. Passes the back side when present (info is often split front/back). Fails
   * open — on any error or a reported extraction failure it returns an empty identity, so the
   * pre-review is skipped and the user proceeds (Veriff/Serpro stays the authoritative check).
   */
  private async ocrIdentityFromFrontId(frontId: string, backId?: string): Promise<KycPreReviewIdentity> {
    const empty: KycPreReviewIdentity = { fullName: null, cpf: null, cpfDigits: null, dobIso: null };
    try {
      const ocr = await this.ks.extractOcrIdentity(frontId, backId);
      this.trace.log(
        'ocr',
        !ocr || ocr.extractionFailed ? 'warn' : 'info',
        !ocr || ocr.extractionFailed ? 'OCR pre-check: no usable extraction (proceeding)' : 'OCR pre-check extracted identity',
        {
          // PII masked — never the full name/CPF; alternates as a count only.
          name: this.maskName(ocr?.fullName),
          cpf: this.maskCpf(ocr?.cpf ? ocr.cpf.replace(/\D/g, '') : null),
          dob: ocr?.dob ?? null,
          nameAlternatesCount: Array.isArray(ocr?.nameAlternates) ? ocr!.nameAlternates!.length : 0,
          documentType: ocr?.documentType ?? null,
          confidence: ocr?.confidence ?? null,
          extractionFailed: ocr?.extractionFailed ?? null,
        },
      );
      // #3 A missing response or a reported extraction failure means the pre-check could not run —
      // flag it as degraded (distinct from a clean-but-empty read) so the caller can make it visible.
      if (!ocr || ocr.extractionFailed) {
        this.ocrDegraded = true;
        return empty;
      }
      const cpf = ocr.cpf ? normalizeCpf(ocr.cpf) : null;
      const cpfDigits = cpf
        ? cpf.replace(/\D/g, '')
        : ocr.cpf
        ? ocr.cpf.replace(/\D/g, '')
        : null;
      return {
        fullName: ocr.fullName ?? null,
        cpf,
        cpfDigits,
        dobIso: ocr.dob ? normalizeDate(ocr.dob) ?? ocr.dob : null,
        // Keep every OCR name candidate so the pre-check scorer can pick the best match. The modal
        // still renders only `fullName`.
        nameAlternates: ocr.nameAlternates ?? undefined,
      };
    } catch (err) {
      // #3 Transport failure (network / 401 / 5xx / CORS) — the pre-check could not run. Keep failing
      // open, but flag it so the caller confirms with the user rather than proceeding silently.
      this.ocrDegraded = true;
      console.warn('[SOMOS KYC V1] OCR pre-check failed (non-fatal, proceeding):', err);
      return empty;
    }
  }

  /**
   * Resolve the no-QR rough-match tolerance. Reads per-action overrides from
   * `cp_actions.config.somosOcrPreCheck` (the same `cp_actions.config` blob the base reads for
   * maxRetries) and falls back to the hardcoded defaults when the action has no override or the
   * lookup fails. `config.somosOcrPreCheck` may set `nameTokenOverlapThreshold` and/or
   * `cpfMaxDigitDistance`; each missing key falls back independently.
   */
  private async resolveOcrTolerance(): Promise<KycPreReviewTolerance> {
    const defaults = SOMOS_OCR_PRE_REVIEW_TOLERANCE_DEFAULTS;
    try {
      if (!this.data.action_id) return defaults;
      const { data } = await this.ks.supabase
        .from('cp_actions')
        .select('config')
        .eq('id', this.data.action_id)
        .single();
      const cfg = ((data?.config as any)?.somosOcrPreCheck ?? {}) as Partial<KycPreReviewTolerance>;
      return {
        nameTokenOverlapThreshold:
          typeof cfg.nameTokenOverlapThreshold === 'number'
            ? cfg.nameTokenOverlapThreshold
            : defaults.nameTokenOverlapThreshold,
        cpfMaxDigitDistance:
          typeof cfg.cpfMaxDigitDistance === 'number'
            ? cfg.cpfMaxDigitDistance
            : defaults.cpfMaxDigitDistance,
      };
    } catch (err) {
      console.warn('[SOMOS KYC V1] Failed to read OCR tolerance from action config; using defaults:', err);
      return defaults;
    }
  }

  /**
   * Resolve the no-QR weighted-score weights/thresholds. Reads per-action overrides from the same
   * `cp_actions.config.somosOcrPreCheck` blob `resolveOcrTolerance` uses and falls back to
   * SOMOS_OCR_SCORE_WEIGHTS_DEFAULTS per key. Any number key on the blob (e.g. `proceedThreshold`,
   * `namePerTokenMatch`, `cpfVeryDifferent`) overrides its default independently.
   */
  private async resolveOcrScoreWeights(): Promise<SomosOcrScoreWeights> {
    const defaults = SOMOS_OCR_SCORE_WEIGHTS_DEFAULTS;
    try {
      if (!this.data.action_id) return defaults;
      const { data } = await this.ks.supabase
        .from('cp_actions')
        .select('config')
        .eq('id', this.data.action_id)
        .single();
      const cfg = ((data?.config as any)?.somosOcrPreCheck ?? {}) as Partial<SomosOcrScoreWeights>;
      const out = { ...defaults };
      (Object.keys(defaults) as Array<keyof SomosOcrScoreWeights>).forEach((k) => {
        if (typeof cfg[k] === 'number') out[k] = cfg[k] as number;
      });
      return out;
    } catch (err) {
      console.warn('[SOMOS KYC V1] Failed to read OCR score weights from action config; using defaults:', err);
      return defaults;
    }
  }

  /**
   * QR path only: returns true when the QR carried NO name and every identity field it DID carry
   * (CPF, DOB) already matches the user's stored PII — i.e. there is nothing left to confirm. Used
   * to skip the pre-review modal for documents like the CIN whose QR encodes only CPF + DOB.
   *
   * Conservative by design: it returns false (→ show the modal) if the QR carried a name, if any
   * carried field mismatches, if the user has no stored value to compare against, or if the QR
   * carried no comparable field at all.
   */
  private qrMatchesWithoutName(
    extracted: KycPreReviewIdentity,
    user: KycPreReviewIdentity,
  ): boolean {
    // Only applies when the QR yielded no name.
    if (extracted.fullName && extracted.fullName.trim()) return false;

    let comparedAtLeastOne = false;

    // CPF: compare on digits only.
    if (extracted.cpfDigits) {
      if (!user.cpfDigits || extracted.cpfDigits !== user.cpfDigits) return false;
      comparedAtLeastOne = true;
    }

    // DOB: compare normalised ISO dates.
    if (extracted.dobIso) {
      const a = normalizeDate(extracted.dobIso) ?? extracted.dobIso;
      const b = user.dobIso ? normalizeDate(user.dobIso) ?? user.dobIso : null;
      if (!b || a !== b) return false;
      comparedAtLeastOne = true;
    }

    return comparedAtLeastOne;
  }

  /** Load the user's stored identity (cp_pii) for the pre-review comparison. */
  private async loadUserIdentity(): Promise<KycPreReviewIdentity> {
    const empty: KycPreReviewIdentity = { fullName: null, cpf: null, cpfDigits: null, dobIso: null };
    try {
      const userId = this.data.ref || (await this.us.getCurrentUser())?.id || '';
      if (!userId) return empty;
      const { data } = await this.ks.supabase
        .from('cp_pii')
        .select('id_document_value, date_of_birth, legal_name, attributes')
        .eq('user_id', userId)
        .maybeSingle();
      const attrs = (data?.attributes as any) || {};
      const composed = `${attrs.first_name ?? ''} ${attrs.surname ?? ''}`.trim();
      const fullName = (data?.legal_name || composed || '').trim() || null;
      const cpfDigits = (data?.id_document_value || '').replace(/\D/g, '') || null;
      const cpf = cpfDigits ? normalizeCpf(cpfDigits) ?? data?.id_document_value ?? null : null;
      return { fullName, cpf, cpfDigits, dobIso: data?.date_of_birth || null };
    } catch (err) {
      console.warn('[SOMOS KYC V1] Failed to load user identity:', err);
      return empty;
    }
  }

  /**
   * Adopt the document's extracted identity into the user's stored PII by calling the profile-update
   * endpoint (`UserService.updateProfile` → POST /profile) with the document's values. Sends the core
   * identity fields only (legal_name, CPF, date_of_birth) so other PII `attributes` (e.g. KYC retry
   * config) aren't clobbered.
   *
   * `overwrite` controls how much is written:
   *  - `true`  (Amend): replace each field the document provided, even if the user already has one.
   *  - `false` (Confirm): fill gaps only — write a field only when the user's value is currently
   *    blank, so a confirmed but slightly-off OCR read never destroys correct stored data.
   */
  private async adoptExtractedPii(
    id: KycPreReviewIdentity,
    current: KycPreReviewIdentity,
    opts: { overwrite: boolean },
  ): Promise<void> {
    const pii: any = {};
    const take = (value: string | null, existing: string | null): boolean =>
      !!value && (opts.overwrite || !existing);
    // #14 Never clobber stored PII with an OCR/QR misread: validate each field before writing it and
    // skip (leave the stored value) when it's invalid. CPF must pass check-digit validation, DOB must
    // parse to a real date, name must be non-empty after trim.
    const isRealDate = (v: string | null): boolean => !!v && !Number.isNaN(Date.parse(v));
    const skipped: string[] = [];
    if (take(id.fullName, current.fullName)) {
      // CPK-4199: the name gets the same treatment as CPF and DOB. A bare non-empty check let an
      // OCR read of a bilingual ID label ("NOME / NAME") be stored as the client's legal name.
      if (isPlausibleLegalName(id.fullName)) pii.legal_name = id.fullName!.trim();
      else skipped.push('name');
    }
    if (take(id.cpfDigits, current.cpfDigits)) {
      if (isValidCpf(id.cpfDigits)) pii.id_document_value = id.cpfDigits;
      else skipped.push('cpf');
    }
    if (take(id.dobIso, current.dobIso)) {
      if (isRealDate(id.dobIso)) pii.date_of_birth = id.dobIso;
      else skipped.push('dob');
    }
    if (skipped.length) {
      this.trace.log('precheck', 'warn', 'Skipped adopting invalid document field(s) into PII', { skipped });
    }
    if (!Object.keys(pii).length) return;
    console.log(`[SOMOS KYC V1] Updating PII from document (overwrite=${opts.overwrite}):`, pii);
    try {
      await this.us.updateProfile({ pii });
    } catch (err) {
      console.warn('[SOMOS KYC V1] Failed to adopt extracted PII (non-fatal):', err);
    }
  }

  private async logPreReviewOutcome(
    outcome:
      | 'auto_proceed'
      | 'reconcile'
      | 'proceed'
      | 'restart'
      | 'reject'
      | 'degraded_proceed'
      | 'degraded_restart',
    extracted: KycPreReviewIdentity,
    user?: KycPreReviewIdentity,
    score?: PreCheckScoreResult,
    explanation?: string,
  ): Promise<void> {
    const detail = {
      outcome,
      provider: 'somoskyc_v1',
      explanation: explanation ?? null,
      // Weighted pre-check analytics (no-QR path). `score`/`decision`/`breakdown` are null on the QR
      // path which has no score.
      score: score?.score ?? null,
      decision: score?.decision ?? null,
      breakdown: score?.breakdown ?? null,
      // PII masked — never the full CPF/name (see maskCpf/maskName). DOB kept for mismatch diagnosis.
      extracted: { name: this.maskName(extracted.fullName), cpf: this.maskCpf(extracted.cpfDigits), dob: extracted.dobIso },
      onFile: { name: this.maskName(user?.fullName), cpf: this.maskCpf(user?.cpfDigits ?? null), dob: user?.dobIso ?? null },
      // Full capture diagnostics (document · method · QR/OCR · signature · SERPRO cert source).
      capture: this.lastCaptureDiag,
    };
    console.warn('[SOMOS KYC V1] pre-review outcome:', detail);
    // A high-confidence auto-pass is its own analytics event; every modal non-confirmation
    // (proceed / restart / amend) shares the existing "not confirmed" event.
    const eventType =
      outcome === 'auto_proceed'
        ? EventTypes.KYC_PRECHECK_AUTO_PROCEED
        : EventTypes.KYC_PRECHECK_NOT_CONFIRMED;
    try {
      await this.events.sendEventWithCaseId(
        eventType,
        this.data.claim_id,
        detail,
      );
    } catch (err) {
      console.warn('[SOMOS KYC V1] Failed to log pre-review event (non-fatal):', err);
    }
  }

  private fold(s: string): string {
    return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  }

  /** Convert a data URL to a File for the existing upload path. */
  private async dataUrlToFile(dataUrl: string, name: string): Promise<File> {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], name, { type: blob.type || 'image/jpeg' });
  }
}
